import { IconCheckCircle } from "@/assets/icons/geist/IconCheckCircle";
import { IconError } from "@/assets/icons/geist/IconError";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { IconWarningCircle } from "@/assets/icons/geist/IconWarningCircle";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import type { PlainPrebuild, ProjectPrebuildsStatus } from "@/queries/prebuild-queries";
import { PrebuildPhase } from "gitpod-next-api/gitpod/v1/prebuild_pb";
import { type FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";

export type PrebuildStatusType = "success" | "error" | "warning" | "in-progress" | "none";

type PrebuildStatusIndicatorProps = {
    projectId: string;
    prebuildsStatus?: ProjectPrebuildsStatus;
    className?: string;
    /** If provided, only show status for prebuilds from these environment classes */
    enabledEnvironmentClassIds?: string[];
};

function getPrebuildStatusType(prebuild?: PlainPrebuild): PrebuildStatusType {
    if (!prebuild) {
        return "none";
    }

    const phase = prebuild.status?.phase;
    const hasWarning = !!prebuild.status?.warningMessage;

    switch (phase) {
        case PrebuildPhase.COMPLETED:
            return hasWarning ? "warning" : "success";
        case PrebuildPhase.FAILED:
            return "error";
        case PrebuildPhase.CANCELLED:
        case PrebuildPhase.DELETED:
            return "none";
        case PrebuildPhase.PENDING:
        case PrebuildPhase.STARTING:
        case PrebuildPhase.RUNNING:
        case PrebuildPhase.STOPPING:
        case PrebuildPhase.SNAPSHOTTING:
        case PrebuildPhase.CANCELLING:
        case PrebuildPhase.DELETING:
            return "in-progress";
        default:
            return "none";
    }
}

/**
 * Computes the aggregate status across multiple prebuilds.
 * Priority: error > warning > in-progress > success > none
 */
function getAggregateStatus(prebuilds: PlainPrebuild[]): PrebuildStatusType {
    if (prebuilds.length === 0) {
        return "none";
    }

    let hasError = false;
    let hasWarning = false;
    let hasInProgress = false;
    let hasSuccess = false;

    for (const prebuild of prebuilds) {
        const status = getPrebuildStatusType(prebuild);
        switch (status) {
            case "error":
                hasError = true;
                break;
            case "warning":
                hasWarning = true;
                break;
            case "in-progress":
                hasInProgress = true;
                break;
            case "success":
                hasSuccess = true;
                break;
        }
    }

    // Priority: error > warning > in-progress > success
    if (hasError) return "error";
    if (hasWarning) return "warning";
    if (hasInProgress) return "in-progress";
    if (hasSuccess) return "success";
    return "none";
}

/**
 * Generates a helpful tooltip message describing the prebuild status across environment classes.
 */
function getTooltipContent(prebuilds: PlainPrebuild[]): JSX.Element | string {
    if (prebuilds.length === 0) {
        return "No prebuilds";
    }

    // Count statuses
    let succeeded = 0;
    let failed = 0;
    let warnings = 0;
    let inProgress = 0;

    for (const prebuild of prebuilds) {
        const status = getPrebuildStatusType(prebuild);
        switch (status) {
            case "success":
                succeeded++;
                break;
            case "error":
                failed++;
                break;
            case "warning":
                warnings++;
                break;
            case "in-progress":
                inProgress++;
                break;
        }
    }

    const total = prebuilds.length;
    const parts: string[] = [];

    if (failed > 0) {
        parts.push(`${failed} failed`);
    }
    if (warnings > 0) {
        parts.push(`${warnings} with warnings`);
    }
    if (inProgress > 0) {
        parts.push(`${inProgress} in progress`);
    }
    if (succeeded > 0) {
        parts.push(`${succeeded} succeeded`);
    }

    if (total === 1) {
        // Single environment class - simpler message
        if (failed > 0) return "Last prebuild failed";
        if (warnings > 0) return "Last prebuild completed with warnings";
        if (inProgress > 0) return "Prebuild in progress";
        if (succeeded > 0) return "Last prebuild succeeded";
        return "No prebuilds";
    }

    // Multiple environment classes
    const summary = parts.join(", ");
    return (
        <span>
            {summary}
            <span className="block text-content-secondary">across {total} environment classes</span>
        </span>
    );
}

/**
 * Displays a status indicator icon for the latest prebuilds of a project.
 * Shows an aggregate status across all environment classes.
 * Clicking navigates to the project's prebuilds page.
 */
export const PrebuildStatusIndicator: FC<PrebuildStatusIndicatorProps> = ({
    projectId,
    prebuildsStatus,
    className,
    enabledEnvironmentClassIds,
}) => {
    const navigate = useNavigate();

    // Filter prebuilds to only include those from enabled environment classes
    const prebuilds = useMemo(() => {
        const all = prebuildsStatus?.all ?? [];
        if (!enabledEnvironmentClassIds?.length) {
            return all;
        }
        const enabledSet = new Set(enabledEnvironmentClassIds);
        return all.filter((pb) => {
            const classId = pb.metadata?.environmentClassId;
            return classId && enabledSet.has(classId);
        });
    }, [prebuildsStatus, enabledEnvironmentClassIds]);
    const status = useMemo(() => getAggregateStatus(prebuilds), [prebuilds]);
    const tooltipContent = useMemo(() => getTooltipContent(prebuilds), [prebuilds]);

    if (status === "none") {
        return null;
    }

    const icon = (() => {
        switch (status) {
            case "success":
                return <IconCheckCircle size="sm" className="text-content-green" />;
            case "error":
                return <IconError size="sm" className="text-content-negative" />;
            case "warning":
                return <IconWarningCircle size="sm" className="text-content-warning" />;
            case "in-progress":
                return <IconSpinner size="sm" className="animate-spin text-content-secondary" />;
            default:
                return null;
        }
    })();

    if (!icon) {
        return null;
    }

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/projects/${projectId}/prebuilds`);
    };

    return (
        <Tooltip content={tooltipContent} side="top" className="font-sans" usePortal>
            <button
                type="button"
                onClick={handleClick}
                className={cn("inline-flex items-center", className)}
                aria-label="View prebuilds"
                data-tracking-id="prebuild-status-indicator"
            >
                {icon}
            </button>
        </Tooltip>
    );
};
